import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, Injector, Signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { environment } from '@env/environment';
import { ApiClient } from '@shared/api-client';
import { NotificationService } from '@shared/services/notification.service';
import { jwtDecode } from 'jwt-decode';
import { BehaviorSubject, catchError, finalize, map, Observable, of, switchMap, tap } from 'rxjs';
import { UserInfoDetailModel } from '../user/api/api.user.model';

export interface JWTPayload {
  exp: number;
  [key: string]: any;
}

const USERINFO = 'userinfo';
const MOCK_DATA_USER: UserInfoDetailModel = {
  id: 'mock-data-user',
  usercode: 'MOCK_DATA',
  email: 'mock.developer@ihi.local',
  firstname: 'Mock',
  lastname: 'Developer',
  fullname: 'Mock Developer',
  phone: '',
  companyid: '',
  company: '',
  country: '',
  position: '',
  division: '',
  languageid: '',
  language: 'English',
  roleid: '',
  role: 'IHI Global Security',
  avatarurl: '',
  groupsname: '',
  groups: null,
  managedcountriesname: '',
  managedcountries: null,
  secondment: false,
  needPasswordChange: false,
  trips: 0,
  created_at: new Date(),
  created_by: 'MOCK_DATA',
  modified_at: new Date(),
  modified_by: 'MOCK_DATA',
};

@Injectable({
  providedIn: 'root',
})
export class LoginService {
  httpClient = inject(HttpClient);
  injector = inject(Injector);
  router = inject(Router);
  notificationService = inject(NotificationService);
  isLogin = new BehaviorSubject(false);
  lang = localStorage.getItem('lang');
  isLoadingGetToken = false;
  private accessToken: string | null = null;
  private sessionInvalidated = false;
  private logoutInProgress = false;
  private readonly api: ApiClient;
  public myInfo: Signal<any> = computed(() => {});

  constructor() {
    this.api = new ApiClient(this.httpClient, { responseTimeout: environment.API_TIMEOUT });
    if (environment.MOCK_DATA) {
      this.setMockDataSession();
    }
  }

  getMyInfo(): Observable<any> {
    return this.httpClient.get<any>(`${environment.API_URL}/user-service/getMyInfo`);
  }
  linkLogin(): Observable<any> {
    return this.httpClient.get<any>(
      `${environment.API_URL}/authenticate-service/getEntraSigninUrl?type=web`,
    );
  }

  getUserInfoLocal(): UserInfoDetailModel | null {
    if (environment.MOCK_DATA) {
      this.setMockDataSession();
    }

    if (localStorage.getItem(USERINFO)) {
      return JSON.parse(localStorage.getItem(USERINFO) || '') as UserInfoDetailModel;
    }
    return null;
  }

  GetInfo() {
    if (environment.MOCK_DATA) {
      this.setMockDataSession();
      return;
    }

    const Info = rxResource({
      stream: () =>
        this.getMyInfo().pipe(
          tap(value => {
            const data = value?.data;
            if (data?.needchangepassword) {
              this.router.navigate(['/auth/changePassword']);
            }
            localStorage.setItem(USERINFO, JSON.stringify(data));
          }),
          catchError(error => {
            if (error.status !== 0) {
              this.router.navigate(['/auth/login']);
            }
            throw new Error(error.name);
          }),
        ),
      injector: this.injector,
    });
    this.myInfo = computed(() => Info.value()?.data || null);
  }

  getLinkLogin(route?: boolean) {
    let url;
    const link = rxResource({
      stream: () =>
        this.linkLogin().pipe(
          switchMap(async value => {
            if (value) {
              const codeVerifier = this.generateCodeVerifier();
              const codeChallenge = await this.generateCodeChallenge(codeVerifier);
              url = this.checkChallengeCode(value.data.url, codeChallenge, codeVerifier);
              window.location.href = url;
            }
            return of(url.toString());
          }),
        ),
      injector: this.injector,
    });
    return computed(() => link.value());
  }

  setAccessToken(token: string) {
    if (token) {
      this.accessToken = token;
      this.sessionInvalidated = false;
      this.logoutInProgress = false;
    }
  }

  getAccessToken() {
    if (environment.MOCK_DATA) return 'mock-data-token';
    if (this.accessToken) return this.accessToken;
    return '';
  }

  removeToken() {
    this.accessToken = null;
  }

  isSessionInvalidated(): boolean {
    return this.sessionInvalidated;
  }

  getToken(codeVerifier: string, codeB2C: string): Observable<boolean> {
    this.isLoadingGetToken = true;
    return this.api.user.getToken(codeB2C, codeVerifier).pipe(
      tap(res => {
        if (res) {
          this.setAccessToken(res.data.access);
          this.isLogin.next(true);
          this.GetInfo();
          this.clearAllParams();
        }
      }),
      map(res => !!res?.data?.access),
      catchError(error => {
        this.router.navigate(['/auth/login']);
        return of(false);
      }),
      finalize(() => (this.isLoadingGetToken = false)),
    );
  }

  refreshToken(): Observable<boolean> {
    if (environment.MOCK_DATA) {
      this.setMockDataSession();
      return of(true);
    }

    this.isLoadingGetToken = true;
    return this.api.user.refreshToken().pipe(
      tap(res => {
        if (res) {
          this.setAccessToken(res.data.access);
          this.isLogin.next(true);
          this.GetInfo();
        }
      }),
      map(res => !!res?.data?.access),
      catchError(error => {
        this.router.navigate(['/auth/login']);
        return of(false);
      }),
      finalize(() => (this.isLoadingGetToken = false)),
    );
  }
  async logoutWithUnregister() {
    try {
      await this.notificationService.unregisterDevice();
    } finally {
      this.logout();
    }
  }

  logout() {
    if (environment.MOCK_DATA) {
      this.setMockDataSession();
      return;
    }

    if (this.logoutInProgress || this.sessionInvalidated) return;

    this.logoutInProgress = true;
    this.sessionInvalidated = true;
    this.isLogin.next(false);
    localStorage.clear();

    this.api.user.logout().subscribe({
      next: res => {
        this.logoutInProgress = false;
        if (res?.data?.logout_url) {
          this.removeToken();
          const url = `${res.data.logout_url}?post_logout_redirect_uri=${res.data.redirect_url}`;
          window.location.href = url;
        } else {
          this.removeToken();
          this.router.navigate(['/auth/login']);
        }
      },
      error: err => {
        this.logoutInProgress = false;
        this.removeToken();
        this.router.navigate(['/auth/login']);
      },
    });
  }

  isTokenExpired(token: string): boolean {
    if (environment.MOCK_DATA) {
      return false;
    }

    if (!token) {
      return true; // No token means "expired"
    }

    try {
      const decoded = jwtDecode<JWTPayload>(token);
      if (!decoded.exp) {
        return true; // No exp means can't verify, treat as expired
      }

      const now = Math.floor(Date.now() / 1000);
      return decoded.exp < now;
    } catch {
      return true; // Invalid token format
    }
  }

  getB2CCode(): Observable<boolean> {
    let url = window.location.href;
    if (!url) return of(false);

    // Decode URL to convert %23 to #
    url = decodeURI(url);
    const parsedUrl = new URL(url);

    // Extract query param "state"
    const state = parsedUrl.searchParams.get('state') || '';
    const token = parsedUrl.searchParams.get('code') || '';
    // Extract fragment (after '#')
    //let hash = '';
    // let token = '';

    // if (parsedUrl.hash) {
    //   const fragment = parsedUrl.hash.substring(1); // remove '#'
    //   const [hashPart, tokenPart] = fragment.split('&');
    //   hash = hashPart || '';
    //   token = tokenPart?.split('=')[1] || '';
    // }
    //if (!state || !token) return;
    if (!state || !token) return of(false);
    return this.getToken(state, token);
  }

  clearAllParams() {
    const url = new URL(window.location.href);
    url.search = ''; // Clear all query parameters
    window.history.replaceState({}, '', url);
  }

  generateCodeVerifier(length = 128) {
    const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
    let result = '';
    const array = new Uint32Array(length);
    window.crypto.getRandomValues(array);
    for (let i = 0; i < length; i++) {
      result += charset[array[i] % charset.length];
    }
    return result;
  }
  async generateCodeChallenge(codeVerifier) {
    const encoder = new TextEncoder();
    const data = encoder.encode(codeVerifier);
    const digest = await window.crypto.subtle.digest('SHA-256', data);
    const base64String = btoa(String.fromCodePoint(...new Uint8Array(digest)))
      .replaceAll('+', '-')
      .replaceAll('/', '_')
      .replace(/={1,2}$/, '');
    return base64String;
  }
  checkChallengeCode(urlString: string, code_challenge: string, code_verifier: string) {
    let url = new URL(urlString);
    url.searchParams.append('state', code_verifier);
    url.searchParams.append('code_challenge', code_challenge);
    url.searchParams.append('code_challenge_method', 'S256');
    return url;
  }

  private setMockDataSession(): void {
    this.accessToken = 'mock-data-token';
    this.sessionInvalidated = false;
    this.logoutInProgress = false;
    this.isLogin.next(true);
    if (!localStorage.getItem('lang')) {
      localStorage.setItem('lang', 'en');
    }
    localStorage.setItem(USERINFO, JSON.stringify(MOCK_DATA_USER));
  }
}
